import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Video } from "@/components/common/video";
import { Spinner } from "@/components/ui/modal";
import VideoController from "./video-controller";
import Panel from "./right-panel";
import { AgentV2TemplateDetailData } from "@/services/agent-v2-template-detail";
import { TemplateVideoAddModal } from "../agent-dashboard-user-detail/template-video-add-modal";
import { StyleData } from "@/services/agent-v2-style-list-local";
import { agentTemplateHooks, useUserManagementHooks } from "@/manager";
import { Image } from '@/components/common/image';
import { TemplateImageAddModal } from "../agent-dashboard-user-detail/template-image-add-modal";
import { LarkImageTableItemsData } from "@/services/agent-v2-feishu-tuwen-table-data";
import { cn } from "@/lib/utils";
import articleTempCover from '@/assets/agent-user-detail/article-temp-cover.png'
import { SETTING_TABS } from "@/manager/manager/agent-dashboard-user-management-manager";

interface TemplateDetailModalVideoProps<T> {
  template: AgentV2TemplateDetailData | null;
  loading?: boolean;
  open: boolean;
  actors: Actor[];
  voices: Audio[];
  deleteLoading?: boolean;
  updateLoading?: boolean;
  onUpdate?: (data: { data: T | null, actor: Actor | null }) => Promise<void>;
  onVideoEdit?: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
  deleteTemplate?: (templateId: string) => Promise<void>;
}

/** 模板详情弹窗 - 视频部分 */
export function TemplateDetailModalVideo<T extends object>({
  open,
  template,
  actors,
  voices,
  loading = false,
  deleteLoading = false,
  updateLoading = false,
  onUpdate,
  onVideoEdit,
  deleteTemplate,
  onOpenChange,
}: TemplateDetailModalVideoProps<T>) {
  const { t } = useTranslation();
  const { activeTab, changeActiveTab } = useUserManagementHooks.useActiveSettingTab(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [load, setLoad] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [selectedStyle, setSelectedStyle] = useState<StyleData | null>(null);
  const [selectedImage, setSelectedImage] = useState<LarkImageTableItemsData | null>(null);
  const [selectedActor, setSelectedActor] = useState<Actor | null>(null);

  const handleDelete = async () => {
    try {
      await deleteTemplate?.(template?.uuid || '');
      onOpenChange(false);
    } catch (error) {
      console.warn("Delete failed:", error);
    }
  }

  const handleEdit = () => {
    if (activeTab === SETTING_TABS.video) {
      onVideoEdit?.()
      onOpenChange(false);
      // setSelectedStyle({
      //   styleId: template.styleModelId,
      //   name: template.templateName,
      //   coverUrl: template.coverUrl,
      //   demoUrl: template.coverVideo,
      // });
      return;
    } else if (activeTab === SETTING_TABS.image) {
      setSelectedImage({
        recordId: template.uuid,
        fields: {
          templateName: template.templateName,
          fileurl: template.coverUrl,
          templateId: template.uuid,
        },
      });
    }
    setSelectedActor(actors.find(actor => actor.modelId === template.actorModelId) || null);
    setOpenAddModal(true);
  }

  const handleVideoSave = async ({ data, actor }: { data: StyleData, actor: Actor }) => {
    try {
      await onUpdate?.({ data: data as T, actor });
      setOpenAddModal(false);
    } catch (error) {
      console.warn("Update failed:", error);
    }
  }

  const handleImageSave = async ({ data, actor }: { data: LarkImageTableItemsData, actor: Actor }) => {
    try {
      await onUpdate?.({ data: data as T, actor });
      setOpenAddModal(false);
    } catch (error) {
      console.warn("Update failed:", error);
    }
  }

  // 自动播放视频
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !open) return;

    const handleCanPlayThrough = async () => {
      try {
        await video.play();
      } catch (error) {
        console.warn("Auto-play failed:", error);
      }
    };

    video.addEventListener("canplaythrough", handleCanPlayThrough);

    if (video.readyState >= 3) {
      handleCanPlayThrough();
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [open, template?.coverVideo]);

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (open) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={(value) => onOpenChange(value)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[840px] h-[750px] max-w-[1055px] max-h-[750px] rounded-2xl shadow-lg z-50 flex overflow-hidden">
          <div
            className={cn(
              "group relative flex-1 bg-gray-200 flex flex-col items-center justify-center overflow-hidden", // 添加 overflow-hidden 防止溢出
              loading && "bg-gray-200"
            )}
          >
            {!loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                {template?.coverVideo ? (
                  <Video
                    ref={videoRef}
                    src={template?.coverVideo}
                    // 这里的 object-cover 会裁切以撑满容器，object-contain 则会完整显示但留黑边
                    className="h-full w-full object-cover"
                    poster={template?.coverUrl}
                    autoPlay
                    preload="auto"
                  />
                ) : (
                  <Image
                    src={template?.coverUrl}
                    alt={template?.templateName}
                    // 确保图片撑满
                    className="h-full w-full object-cover"
                  />
                )}

                {/* 加载状态层 */}
                {template?.coverVideo && load && (
                  <div className="absolute inset-0 flex justify-center items-center bg-black/20">
                    <Spinner className="text-white" size={32} />
                  </div>
                )}

                {/* 控制条 */}
                {template?.coverVideo && (
                  <div className="opacity-0 group-hover:opacity-100 absolute bottom-0 left-0 right-0 transition-opacity duration-250 z-10">
                    <VideoController videoRef={videoRef} onLoad={setLoad} />
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            ref={titleRef}
            tabIndex={-1} className="hidden"></div>
          {/* 右侧信息面板 */}
          <Panel
            onEdit={handleEdit}
            template={template}
            deleteLoading={deleteLoading}
            loading={loading}
            activeTab={activeTab}
            onDelete={handleDelete}
            onClose={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
      {activeTab === SETTING_TABS.video && (
        <TemplateVideoAddModal
          title={t("agent_dashboard.user_detail.template.edit_template")}
          open={openAddModal}
          loading={updateLoading}
          selectedStyle={selectedStyle}
          selectedActor={selectedActor}
          onOpenChange={setOpenAddModal}
          actors={actors}
          voices={voices}
          onSave={handleVideoSave}
        />
      )}
      {activeTab === SETTING_TABS.image && (
        <TemplateImageAddModal
          title={t("agent_dashboard.user_detail.template.edit_template")}
          open={openAddModal}
          loading={updateLoading}
          selectedTemplate={selectedImage}
          selectedActor={selectedActor}
          onOpenChange={setOpenAddModal}
          actors={actors}
          voices={voices}
          onSave={handleImageSave}
        />
      )}
    </Dialog.Root>
  );
}